import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { formatApiError } from "../lib/api";
import { Leaf } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success("Account created");
      nav("/onboarding");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="flex items-center justify-center p-8 order-2 md:order-1">
        <div className="w-full max-w-sm">
          <h1 className="font-serif-display text-4xl text-stone-900">{t("register_title")}</h1>
          <p className="text-stone-500 mt-2">{t("register_sub")}</p>
          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="register-form">
            <div>
              <Label className="text-stone-700">{t("name")}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} required className="mt-1 rounded-xl bg-stone-50 border-transparent focus:ring-2 focus:ring-[#4A5D23]/30" data-testid="register-name-input" />
            </div>
            <div>
              <Label className="text-stone-700">{t("email")}</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 rounded-xl bg-stone-50 border-transparent focus:ring-2 focus:ring-[#4A5D23]/30" data-testid="register-email-input" />
            </div>
            <div>
              <Label className="text-stone-700">{t("password")}</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="mt-1 rounded-xl bg-stone-50 border-transparent focus:ring-2 focus:ring-[#4A5D23]/30" data-testid="register-password-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-full bg-[#E07A5F] hover:bg-[#C96D55] text-white py-6 btn-pop" data-testid="register-submit-button">
              {loading ? "…" : t("submit_register")}
            </Button>
          </form>
          <p className="mt-6 text-sm text-stone-500">
            {t("have_account")} <Link to="/login" className="text-[#4A5D23] font-semibold" data-testid="link-to-login">{t("submit_login")}</Link>
          </p>
        </div>
      </div>
      <div className="hidden md:block relative order-1 md:order-2">
        <img
          src="https://images.pexels.com/photos/1192031/pexels-photo-1192031.jpeg?auto=compress&cs=tinysrgb&w=1200"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tl from-[#E07A5F]/60 to-transparent" />
        <div className="relative p-12 text-white h-full flex flex-col items-end">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link-2">
            <img src="/logo.png" alt="Gracely" className="w-8 h-8 object-contain brightness-0 invert" />
            <span className="font-serif-display text-2xl">Healthy Gracely</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
